import { useEffect, useState } from "react";

interface Indicator {
    name: string;
    path: string;
}

export function useGetIndicators(
    version: string | undefined,
    exchangeProfile: string | undefined
) {
    const [loading, setLoading] = useState(false);
    const [indicators, setIndicators] = useState<Indicator[]>([]);

    const controller = new AbortController();

    useEffect(() => {
        if (
            !version ||
            !exchangeProfile ||
            version.trim().length === 0 ||
            exchangeProfile.trim().length === 0
        ) {
            setIndicators([]);
            return;
        }

        const fetchExchangeProfiles = async () => {
            setLoading(true);

            const encodedExchangeProfile = encodeURIComponent(exchangeProfile);
            const encodedVersion = encodeURIComponent(version);

            const apiUrl = `${import.meta.env.VITE_PUBLICATIE_API_URL}/v1.1/exchange-profiles/${encodedExchangeProfile}/${encodedVersion}`;

            try {
                const response = await fetch(apiUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    signal: controller.signal,
                });

                const data = await response.json();

                if (response.ok) {
                    const mappedIndicators: Indicator[] = (
                        data.version.indicators || []
                    ).map((indicator: any) => {
                        return {
                            name: indicator.name,
                            path: indicator.path,
                        };
                    });

                    const sortedIndicators = mappedIndicators.sort((a, b) =>
                        a.name.localeCompare(b.name, undefined, {
                            numeric: true,
                            sensitivity: "base",
                        })
                    );

                    setIndicators(sortedIndicators);
                }
            } catch (error) {
                console.error("Error fetching exchange profiles:", error);
            } finally {
                setLoading(false);
            }
        };

        void fetchExchangeProfiles();

        return () => {
            controller.abort();
        };
    }, [version, exchangeProfile]);

    return { loading, indicators };
}

export default useGetIndicators;
